/**
 * Biblioteca Lean nativa — Trabalho Padrão do Líder (TPL)
 */

import type { LstCargo, LstCategory, LstFrequency } from '../types';
import { buildEnrichedImportPayload, getLibraryObjetivo } from '../services/leanLibraryEnrichment';

export type LeanNivel =
  | 'nivel-1-lider-producao'
  | 'nivel-2-supervisor'
  | 'nivel-3-coordenador'
  | 'nivel-4-gerente'
  | 'nivel-5-diretor';

export type LeanCategoria =
  | 'Segurança'
  | 'Qualidade'
  | 'Entrega'
  | 'Custo'
  | 'Pessoas'
  | 'Melhoria Contínua';

export type LeanFrequenciaLabel = 'Diária' | 'Semanal' | 'Mensal' | 'Horária';

export interface LeanLibraryItem {
  id: string;
  titulo: string;
  categoria: LeanCategoria;
  nivelHierarquico: LeanNivel[];
  frequenciaRecomendada: LeanFrequenciaLabel;
  duracaoRecomendada: number;
  descricao: string;
  objetivo: string;
  evidenciaEsperada: string;
  tags: string[];
  origem: 'Biblioteca Lean';
}

/** Nível de maturidade da rotina na jornada Lean. */
export type LeanRoutineMaturity = 'basica' | 'intermediaria' | 'avancada' | 'excelencia';

/** Intensidade de impacto no IEL. */
export type LeanIelImpactLevel = 'baixo' | 'medio' | 'alto' | 'muito_alto';

/** Consultor digital — enriquecimento por rotina (Fase 1+: 100 rotinas). */
export interface LeanLibraryEnrichment {
  /** Passos do SWI sugerido — "Como verificar". */
  swiPassos: string[];
  /** Critérios de conformidade do processo auditado. */
  itensConformidade: string[];
  /** Critérios explícitos de desvio (não conformidade). */
  itensDesvio: string[];
  /** Taxonomia operacional Lean (Gemba, TPM, SMED, etc.). */
  categoriaOperacional: string;
  indicadoresPrimarios: string[];
  indicadoresSecundarios: string[];
  /** Por que medir os indicadores vinculados. */
  relacaoCausal: string;
  /** Benchmark de frequência por nível hierárquico. */
  frequenciaBenchmark: Partial<Record<LeanNivel, LeanFrequenciaLabel>>;
  nivelMaturidade: LeanRoutineMaturity;
  ielImpacto: {
    disciplina: LeanIelImpactLevel;
    /** Conformidade do processo auditado (aderência ao padrão operacional). */
    aderenciaPadrao: LeanIelImpactLevel;
    resultado: LeanIelImpactLevel;
  };
}

export const LEAN_MATURITY_LABELS: Record<LeanRoutineMaturity, string> = {
  basica: 'Básica',
  intermediaria: 'Intermediária',
  avancada: 'Avançada',
  excelencia: 'Excelência',
};

export const LEAN_IEL_IMPACT_LABELS: Record<LeanIelImpactLevel, string> = {
  baixo: 'Baixo',
  medio: 'Médio',
  alto: 'Alto',
  muito_alto: 'Muito Alto',
};

export const LEAN_NIVEL_LABELS: Record<LeanNivel, string> = {
  'nivel-1-lider-producao': 'Nível 1 — Líder de Produção',
  'nivel-2-supervisor': 'Nível 2 — Supervisor',
  'nivel-3-coordenador': 'Nível 3 — Coordenador',
  'nivel-4-gerente': 'Nível 4 — Gerente',
  'nivel-5-diretor': 'Nível 5 — Diretor',
};

export const LEAN_CATEGORIAS: LeanCategoria[] = [
  'Segurança',
  'Qualidade',
  'Entrega',
  'Custo',
  'Pessoas',
  'Melhoria Contínua',
];

export const LEAN_FREQUENCIAS: LeanFrequenciaLabel[] = ['Diária', 'Semanal', 'Mensal', 'Horária'];

/** Todos os cargos LST aceitos pelo banco (lst_templates.target_role). */
export const LEAN_LST_TARGET_ROLES: readonly LstCargo[] = [
  'lider',
  'supervisor',
  'coordenador',
  'gerente',
  'diretor',
  'apoio',
] as const;

export function isLstCargo(value: string): value is LstCargo {
  return (LEAN_LST_TARGET_ROLES as readonly string[]).includes(value);
}

export function isLeanCategoria(value: string): value is LeanCategoria {
  return (LEAN_CATEGORIAS as readonly string[]).includes(value);
}

export function isLeanFrequenciaLabel(value: string): value is LeanFrequenciaLabel {
  return (LEAN_FREQUENCIAS as readonly string[]).includes(value);
}

export function isLeanNivel(value: string): value is LeanNivel {
  return value in LEAN_NIVEL_LABELS;
}

/** Mapeia nível Lean → cargo LST (compatível com lst_templates.target_role). */
export function leanNivelToLstCargo(nivel: LeanNivel): LstCargo {
  switch (nivel) {
    case 'nivel-1-lider-producao':
      return 'lider';
    case 'nivel-2-supervisor':
      return 'supervisor';
    case 'nivel-3-coordenador':
      return 'coordenador';
    case 'nivel-4-gerente':
      return 'gerente';
    case 'nivel-5-diretor':
      return 'diretor';
    default:
      return 'lider';
  }
}

export function leanCategoriaToLstCategory(cat: LeanCategoria): LstCategory {
  switch (cat) {
    case 'Segurança':
      return 'seguranca';
    case 'Qualidade':
      return 'qualidade';
    case 'Entrega':
      return 'producao';
    case 'Custo':
    case 'Pessoas':
    case 'Melhoria Contínua':
      return 'apoio';
    default:
      return 'producao';
  }
}

export function leanFrequenciaToLstFrequency(freq: LeanFrequenciaLabel): LstFrequency {
  switch (freq) {
    case 'Horária':
      return 'horaria';
    case 'Diária':
      return 'diaria';
    case 'Semanal':
      return 'semanal';
    case 'Mensal':
      return 'mensal';
    default:
      return 'diaria';
  }
}

/** Resolve nível Lean a partir do cargo LST + metadados do usuário. */
export function resolveLeanNivelForUser(
  lstCargo: LstCargo,
  roleName?: string | null,
): LeanNivel {
  const name = (roleName ?? '').toLowerCase();
  if (name.includes('diretor') || name.includes('president')) return 'nivel-5-diretor';
  if (name.includes('coordenador')) return 'nivel-3-coordenador';
  if (name.includes('gerente') || lstCargo === 'gerente') return 'nivel-4-gerente';
  if (name.includes('supervisor') || lstCargo === 'supervisor') return 'nivel-2-supervisor';
  if (name.includes('líder') || name.includes('lider') || lstCargo === 'lider') return 'nivel-1-lider-producao';
  return 'nivel-1-lider-producao';
}

export interface LeanLibraryImportPayload {
  title: string;
  description: string | null;
  frequency: LstFrequency;
  category: LstCategory;
  duration_minutes: number;
  activity_kind: 'standard' | 'reuniao_camada';
  swi_text: string | null;
  swi_checklist: Array<{ id: string; text: string }>;
  linked_indicator_name: string | null;
  is_critical: boolean;
}

export function leanItemToTemplatePayload(item: LeanLibraryItem): LeanLibraryImportPayload {
  const isReuniao =
    /reunião|reuniao|camada|one-on-one|gemba walk executivo/i.test(item.titulo) ||
    item.tags.includes('reuniao-camada');

  const isCritical =
    /segurança|seguranca|crític|gargalo|escalonamento|problema crônico/i.test(item.titulo) ||
    item.categoria === 'Segurança';

  const enriched = buildEnrichedImportPayload(item);

  return {
    title: item.titulo,
    description: getLibraryObjetivo(item),
    frequency: leanFrequenciaToLstFrequency(item.frequenciaRecomendada),
    category: leanCategoriaToLstCategory(item.categoria),
    duration_minutes: item.duracaoRecomendada,
    activity_kind: isReuniao ? 'reuniao_camada' : 'standard',
    swi_text: enriched.swi_text,
    swi_checklist: enriched.swi_checklist,
    linked_indicator_name: enriched.linked_indicator_name,
    is_critical: isCritical,
  };
}
